import React from 'react';
import { Eye } from 'lucide-react';

// A simple Tailwind-styled Badge component
const Badge = ({ children, className }) => (
  <span
    className={`inline-flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full ${className}`}
  >
    {children}
  </span>
);

export default function ArtworkCard({ artwork, viewMode = "grid", onNavigate }) {
  if (viewMode === "list") {
    return (
      <div
        onClick={onNavigate}
        className="flex flex-col sm:flex-row gap-6 bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group border border-gray-200 cursor-pointer"
      >
        <div className="sm:w-64 h-48 sm:h-auto flex-shrink-0 relative overflow-hidden">
          <img
            src={artwork.thumbnail_url || artwork.imageUrl}
            alt={artwork.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
            onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/600x400/cccccc/ffffff?text=Image+Error"; }}
          />
        </div>
        <div className="flex-1 p-6">
          <div className="flex flex-wrap gap-2 mb-3">
            {artwork.category && (
              <Badge className="bg-orange-50 text-orange-800 border border-orange-200">
                {artwork.category}
              </Badge>
            )}
            {(artwork.tags || []).slice(0, 2).map((tag, index) => (
              <Badge key={index} className="text-xs bg-gray-100 text-gray-700 border border-gray-200">
                {tag}
              </Badge>
            ))}
          </div>
          <h3 className="text-2xl font-serif font-bold text-gray-800 mb-2 group-hover:text-orange-600 transition-colors">
            {artwork.title}
          </h3>
          <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
            {artwork.medium && <span>{artwork.medium}</span>}
            {artwork.dimensions && <span>• {artwork.dimensions}</span>}
          </div>
          {artwork.description && (
            <p className="text-gray-600 line-clamp-2 mb-4">
              {artwork.description}
            </p>
          )}
          <div className="flex items-center text-orange-600 font-medium">
            <Eye className="w-4 h-4 mr-2" />
            View Details
          </div>
        </div>
      </div>
    );
  }

  // Default is 'grid' view - ORIGINAL DESIGN
  return (
    <div
      onClick={onNavigate}
      className="group block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-300 border border-gray-200 cursor-pointer"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-gray-100">
        <img
          src={artwork.thumbnail_url || artwork.imageUrl}
          alt={artwork.title}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
          loading="lazy"
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/400x500/cccccc/ffffff?text=Image+Error"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute bottom-0 left-0 right-0 p-4 text-white translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-in-out">
          <div className="flex items-center text-sm font-medium">
            <Eye className="w-4 h-4 mr-2" />
            View Details
          </div>
        </div>
      </div>
      <div className="p-5">
        <div className="flex flex-wrap gap-2 mb-2">
          {artwork.category && (
            <Badge className="bg-orange-50 text-orange-800 border border-orange-200 text-xs">
              {artwork.category}
            </Badge>
          )}
        </div>
        <h3 className="text-lg font-serif font-bold text-gray-800 mb-1 group-hover:text-orange-600 transition-colors line-clamp-1">
          {artwork.title}
        </h3>
        <div className="text-xs text-gray-500 space-y-1">
          {artwork.medium && <p>{artwork.medium}</p>}
        </div>
      </div>
    </div>
  );
}